import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useLang } from "../i18n/LanguageContext";
import { IMAGES, PHONE_TEL, waLink } from "../constants/images";
import { Phone } from "lucide-react";
import { EASE } from "./Reveal";

const INTRO = 0.9;

const MaskedLine = ({ children, i }) => (
  <span className="block overflow-hidden pb-[0.06em] -mb-[0.06em]">
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1, delay: INTRO + i * 0.13, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

const RotatingBadge = ({ text }) => (
  <div className="relative w-28 h-28 hidden md:flex items-center justify-center" data-testid="hero-rotating-badge">
    <svg viewBox="0 0 100 100" className="absolute inset-0 w-full h-full tg-spin">
      <defs>
        <path id="circlePath" d="M 50,50 m -38,0 a 38,38 0 1,1 76,0 a 38,38 0 1,1 -76,0" />
      </defs>
      <text className="font-mono2" fill="#94A3B8" fontSize="9.5" letterSpacing="2.5">
        <textPath href="#circlePath">{text}</textPath>
      </text>
    </svg>
    <div className="w-12 h-12 border border-[#D96B27] flex items-center justify-center">
      <span className="font-display font-extrabold text-[#D96B27] text-lg">35+</span>
    </div>
  </div>
);

const Hero = () => {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "24%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1.02, 1.16]);

  return (
    <section ref={ref} className="relative min-h-screen flex flex-col justify-end overflow-hidden" data-testid="hero-section">
      <motion.div className="absolute inset-0" style={{ y, scale }}>
        <img src={IMAGES.hero} alt="BoomGradnja construction team" className="w-full h-full object-cover" />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-t from-[#0D0F12] via-[#0D0F12]/55 to-[#0D0F12]/70" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#0D0F12]/80 via-transparent to-transparent" />
      <div className="tech-grid absolute inset-0 opacity-60" />
      <div className="grain" />

      <div className="relative z-10 max-w-[1400px] w-full mx-auto px-5 sm:px-8 pt-36 pb-10 flex flex-col">
        <div className="flex items-start justify-between gap-6">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: INTRO - 0.25, ease: EASE }}
            className="flex flex-wrap items-center gap-3"
          >
            <span className="font-mono2 text-xs uppercase tracking-[0.3em] text-[#D96B27] border border-[#D96B27]/40 px-3 py-1.5">
              {t.hero.badge}
            </span>
            <span className="font-mono2 text-xs uppercase tracking-[0.3em] text-[#F4F1EA]/70 border border-white/20 px-3 py-1.5">
              {t.hero.locationTag}
            </span>
          </motion.div>
          <div className="shrink-0 pt-2">
            <RotatingBadge text="BOOMGRADNJA • SARAJEVO • 35+ GODINA • " />
          </div>
        </div>

        <h1
          className="mt-6 font-display font-extrabold uppercase leading-[0.95] tracking-tight text-[#F4F1EA] text-[clamp(2.7rem,9vw,7.5rem)]"
          data-testid="hero-title"
        >
          {t.hero.titleLines.map((line, i) => (
            <MaskedLine key={i} i={i}>
              <span className={line.accent ? "text-[#D96B27]" : ""}>{line.t}</span>
            </MaskedLine>
          ))}
        </h1>

        <motion.div
          initial={{ scaleX: 0 }}
          animate={{ scaleX: 1 }}
          transition={{ duration: 1.1, delay: INTRO + 0.45, ease: EASE }}
          className="origin-left h-[3px] w-28 bg-[#D96B27] mt-7"
        />

        <div className="mt-7 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: INTRO + 0.55, ease: EASE }}
            className="max-w-xl text-base sm:text-lg leading-relaxed text-[#F4F1EA]/85"
            data-testid="hero-subtitle"
          >
            {t.hero.subtitle}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: INTRO + 0.7, ease: EASE }}
            className="flex flex-wrap items-center gap-4"
          >
            <a
              data-testid="hero-cta-call"
              href={PHONE_TEL}
              className="group flex items-center gap-3 bg-[#D96B27] hover:bg-[#ED7A33] text-[#0D0F12] font-semibold px-7 py-4 text-base transition-all duration-300 hover:-translate-y-0.5"
            >
              <Phone className="w-5 h-5" />
              {t.hero.ctaCall}
            </a>
            <a
              data-testid="hero-cta-whatsapp"
              href={waLink(t.contact.waMessage)}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 border border-[#F4F1EA]/40 hover:border-[#25D366] hover:text-[#25D366] text-[#F4F1EA] font-semibold px-7 py-4 text-base transition-all duration-300 hover:-translate-y-0.5"
            >
              {t.hero.ctaWhatsapp}
            </a>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: INTRO + 0.9 }}
          className="mt-12 pt-6 border-t border-white/15 grid grid-cols-2 md:grid-cols-4 gap-6"
          data-testid="hero-stats"
        >
          {t.about.stats.map((s, i) => (
            <div key={i}>
              <p className="font-display font-extrabold text-3xl sm:text-4xl text-[#F4F1EA]">{s.v}</p>
              <p className="font-mono2 text-[11px] uppercase tracking-[0.2em] text-[#94A3B8] mt-1.5">{s.l}</p>
            </div>
          ))}
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: INTRO + 1.1 }}
        className="absolute bottom-6 end-6 z-10 hidden lg:flex flex-col items-center gap-2"
      >
        <span className="font-mono2 text-[10px] uppercase tracking-[0.3em] text-[#94A3B8] [writing-mode:vertical-rl]">{t.hero.scrollHint}</span>
        <motion.span animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 1.8 }} className="w-px h-10 bg-[#D96B27]" />
      </motion.div>
    </section>
  );
};

export default Hero;
